import { IApifySetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

export interface IConfigApifySettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IApifySetting;
}

export interface IConfigApifySettingsProps {
  formInputName: string[];
  closeModal: any;
  showModal: boolean;
  editingApifyId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigApifySettings extends React.PureComponent<IConfigApifySettingsProps, IConfigApifySettingsState> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigApifySettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount() {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigApifySettingsProps & IWithSDProps) {
    if (prevProps.editingApifyId !== this.props.editingApifyId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = () => {
    if (!this.props.editingApifyId) return;

    const editingConfig = this.props.commonSettings.apifySettings?.find((x) => x.id === this.props.editingApifyId);

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule, value) => {
    const ApifySetting = this.props.commonSettings?.apifySettings;
    if (!ApifySetting) return;

    const isSettingAlreadyExist = ApifySetting.some((x) => x.name === value && x.id !== this.props.editingApifyId);

    if (isSettingAlreadyExist) throw new Error("Apify Name. is already exist.");
  };

  onFinish = (data: any) => {
    this.saveStatus.start();
    let obj: IApifySetting;
    obj = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey
    };

    if (this.props.editingApifyId) {
      obj.id = this.props.editingApifyId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "apifySettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "apifySettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (errorInfo: any) => {};

  deleteSetting = () => {
    if (!this.props.editingApifyId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingApifyId || "",
      configKey: "apifySettings",

      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Apify setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render() {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Apify setting"
        secondHeader="Apify setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingApifyId ? this.deleteSetting : undefined}
            submitLabel={this.props.editingApifyId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configApifyForm"
          />
        }
      >
        <Form
          id="configApifyForm"
          name="apifySetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_Apify_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_apify_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter a Apify settings Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Apify settings Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_apify_apikey">
              <Form.Item name="apiKey" rules={[{ required: true, message: "Please enter a Apify API Key." }]}>
                <KuikaInput size="md" autoFocus />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
